import { useEffect, useRef, useState } from "react";
import { NavigationArrow, CaretDown, Check } from "@phosphor-icons/react";
import {
  buildDirectionsUrl,
  getDirectionsProviders,
  getStoredDirectionsProvider,
  setStoredDirectionsProvider,
} from "../lib/directions";

/**
 * DirectionsButton — universal "Directions" launcher used on the public
 * profile, map sheet, and landing-page event cards.
 *
 * UX:
 *  - Tap the tile → opens directions in the user's saved/default app
 *    immediately (Apple Maps on iOS/macOS, Google elsewhere first time).
 *  - Tap the small caret overlay (top-right corner on filled variant,
 *    inline on ghost variant) → opens a 3-option picker. Picking saves
 *    the choice to localStorage so every DirectionsButton in the app
 *    uses the same provider from then on.
 *
 * Variants:
 *  - filled:  matches ActionButton's tile shape (h-12 stacked icon+label).
 *             Caret is a small floating chip in the corner.
 *  - ghost:   compact pill used in map sheets / event cards.
 *             Caret is an inline trailing button.
 */
export default function DirectionsButton({
  lat,
  lng,
  address,
  label,
  disabled,
  variant = "filled",
  testId = "action-directions",
  trackOpen,
}) {
  const [provider, setProvider] = useState(() => getStoredDirectionsProvider());
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const isDisabled = disabled || (lat == null && !address) || (lng == null && !address);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Primary tile behaviour (May 2026 update):
  // - If the user has NEVER picked a default provider, tapping the
  //   tile opens the picker. That makes the choice obvious — real
  //   user report: *"It also doesn't give you the option of which
  //   map app to use. I would like the option of Google, Apple Or
  //   Waze"*. Hiding the picker behind a 12px caret wasn't enough.
  // - Once they HAVE picked one (saved to localStorage), tapping
  //   launches that app immediately. The caret stays available to
  //   re-pick whenever.
  const onPrimaryTap = () => {
    if (isDisabled) return;
    if (!provider) {
      setOpen(true);
      return;
    }
    launch(provider);
  };

  const launch = (key) => {
    if (isDisabled) return;
    setProvider(key);
    setStoredDirectionsProvider(key);
    setOpen(false);
    const url = buildDirectionsUrl(key, { lat, lng, address, label });
    if (trackOpen) {
      try { trackOpen(key); } catch { /* never throw into the UI */ }
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const providers = getDirectionsProviders();

  if (variant === "ghost") {
    return (
      <div ref={wrapRef} className="relative inline-flex">
        <div className={`inline-flex items-stretch rounded-full overflow-hidden ${isDisabled ? "opacity-50" : ""}`}>
          <button
            type="button"
            disabled={isDisabled}
            onClick={onPrimaryTap}
            className="px-3 h-8 text-xs font-semibold inline-flex items-center gap-1.5 bg-foreground text-background hover:opacity-90"
            data-testid={testId}
          >
            <NavigationArrow weight="duotone" className="h-3.5 w-3.5" />
            Directions
          </button>
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => setOpen((v) => !v)}
            aria-label="Choose navigation app"
            aria-expanded={open}
            className="px-1.5 h-8 bg-foreground text-background border-l border-background/20 hover:opacity-90"
            data-testid={`${testId}-picker`}
          >
            <CaretDown weight="bold" className="h-3 w-3" />
          </button>
        </div>
        {open && <PickerMenu testId={testId} providers={providers} current={provider} onPick={launch} />}
      </div>
    );
  }

  // Default "filled" variant: matches sibling ActionButton tiles
  // (h-12 rounded-2xl stacked icon+label). Caret is a corner chip.
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={isDisabled}
        onClick={onPrimaryTap}
        data-testid={testId}
        className="w-full h-12 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-primary text-primary-foreground hover:bg-primary/90"
      >
        <NavigationArrow weight="duotone" className="h-4 w-4" />
        Directions
      </button>
      {/* Caret overlay — small chip in the top-right corner that opens
          the picker without changing the tile's footprint in the grid. */}
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((v) => !v)}
        aria-label="Choose navigation app"
        aria-expanded={open}
        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 text-primary-foreground flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testId}-picker`}
      >
        <CaretDown weight="bold" className="h-2.5 w-2.5" />
      </button>
      {open && <PickerMenu testId={testId} providers={providers} current={provider} onPick={launch} />}
    </div>
  );
}

function PickerMenu({ testId, providers, current, onPick }) {
  return (
    <div
      role="menu"
      data-testid={`${testId}-menu`}
      // `text-foreground` is critical — the menu often renders inside
      // a button with `text-primary-foreground` (white). Without an
      // explicit text colour here, every label inherits white and
      // becomes invisible on the white `bg-card`. Real bug report
      // May 2026: "the box is white and so is the text".
      className="absolute right-0 mt-1 z-40 min-w-[200px] rounded-xl border border-border bg-card text-foreground shadow-lg overflow-hidden"
    >
      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-bold border-b border-border">
        {current ? "Open in" : "Choose your map app"}
      </div>
      {providers.map((p) => (
        <button
          key={p.key}
          type="button"
          role="menuitem"
          onClick={() => onPick(p.key)}
          data-testid={`${testId}-option-${p.key}`}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
        >
          <span>{p.label}</span>
          {current === p.key && <Check size={14} weight="bold" className="text-primary" />}
        </button>
      ))}
    </div>
  );
}
